import * as React from 'react';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Switch, Input, Alert, Select } from 'antd';
import { connect } from 'react-redux';
import { FormComponentProps } from '@ant-design/compatible/lib/form/Form';
import { withRouter } from 'react-router';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import BackButton from '../back_button/BackButton';
import OWButton from '../ow_button/OWButton';
import {
	fieldTechsRestCrudThunksForSupplier,
	updateFieldTechDetailsForSupplier,
} from '../../thunks/field_techs_thunks';
import { EPA_CERTIFICATION_TYPE_OPTIONS } from '../../utils/RefrigerantUtils';
import EmailComponentAntdV3 from '../email_component/EmailComponentAntdV3';

require('./FieldTechForm.less');

interface FieldTechFormProps extends FormComponentProps {
	creating: boolean;
	updating: boolean;
	formData: any;
	history: any;
	redirectForwardUrl?: string;
	onSuccess?: any;
	createErrors: any[];
	updateErrors: any[];
	currentUser: any;
	create: any;
	update: any;
	updateDetails: any;
}

class FieldTechForm extends React.Component<FieldTechFormProps, any> {
	handleSubmit = (e, submitAction) => {
		const { history, onSuccess, redirectForwardUrl, formData, updateDetails } = this.props;
		e.preventDefault();
		this.props.form.validateFields((err, values) => {
			if (!err) {
				const fieldTech = {
					...formData,
					...values,
					hourlyRate: values.hourlyRate ? parseFloat(values.hourlyRate) : undefined,
				};

				// The standard PUT update writes the field-tech row only. The name and the EPA 608
				// certification are columns on the CONTACT, so they go through the dedicated
				// contact-details PATCH alongside the update. The edit form captures given/family names
				// and the certification (see render); create still uses the single combined `name`
				// field and registers no certification fields, so this branch only applies on update.
				const isUpdate = formData && formData.email !== undefined;
				const nameChanged =
					isUpdate &&
					(values.nameGiven !== nullSafeGet('supplierContact.nameGiven', formData) ||
						values.nameFamily !== nullSafeGet('supplierContact.nameFamily', formData));
				// Keyed on `in values` rather than on truthiness, because the create variant of this form
				// never registers these fields — there the keys are genuinely absent and the PATCH is not
				// fired at all. When they ARE registered, an `undefined` value means the user cleared the
				// input, and that has to reach the server as an explicit null: an omitted key means "keep
				// what is stored" (see the thunk), so a cleared certification would otherwise survive the
				// save and the tech would keep reading as certified on a credential they no longer hold.
				const certificationChanged =
					isUpdate &&
					('epaCertificationType' in values || 'epaCertificationNumber' in values) &&
					(values.epaCertificationType !==
						nullSafeGet('supplierContact.epaCertificationType', formData) ||
						values.epaCertificationNumber !==
							nullSafeGet('supplierContact.epaCertificationNumber', formData));
				const certificationPayload = certificationChanged
					? {
							epaCertificationType: values.epaCertificationType || null,
							epaCertificationNumber: values.epaCertificationNumber || null,
					  }
					: {};
				const submit = submitAction(fieldTech);
				// ONE contact PATCH covering both, rather than one per changed field: they land on the same
				// row through the same endpoint, and two requests could half-apply.
				const request =
					nameChanged || certificationChanged
						? submit.then((rec) =>
								updateDetails({
									userId: formData.email,
									...(nameChanged && {
										nameGiven: values.nameGiven,
										nameFamily: values.nameFamily,
									}),
									...certificationPayload,
								}).then(() => rec)
						  )
						: submit;

				request.then((record) => {
					if (redirectForwardUrl) {
						history.push(redirectForwardUrl);
					}
					if (onSuccess) {
						onSuccess(record);
					}
				});
			}
		});
	};

	render() {
		const {
			updating,
			creating,
			form,
			update,
			create,
			createErrors,
			formData,
			updateErrors,
			currentUser,
		} = this.props;
		const { getFieldDecorator } = form;
		const isUpdate = formData && formData.email !== undefined;
		const submitAction = isUpdate ? update : create;
		const submitText = isUpdate ? 'Update field technician' : 'Create field technician';
		const isLoading = updating || creating;

		getFieldDecorator('supplierFacilityId', {
			initialValue: formData.supplierFacilityId || nullSafeGet('facility.id', currentUser),
		});
		getFieldDecorator('supplierCompanyId', {
			initialValue:
				formData.supplierCompanyId || nullSafeGet('facility.supplierCompanyId', currentUser),
		});

		getFieldDecorator('id', { initialValue: formData.id });

		return (
			<Form
				layout="vertical"
				onSubmit={(e) => this.handleSubmit(e, submitAction)}
				className="supplierForm"
			>
				{createErrors.length > 0 ? (
					<Form.Item>
						<Alert message={createErrors.join(' ')} type="error" />
					</Form.Item>
				) : null}
				{updateErrors.length > 0 ? (
					<Form.Item>
						<Alert message={updateErrors.join(' ')} type="error" />
					</Form.Item>
				) : null}
				<EmailComponentAntdV3
					form={form}
					formData={formData}
					label={'Email'}
					fieldProps={{
						key: `email`,
						name: 'email',
					}}
					style={{ maxWidth: 640 }}
					disabled={formData.email ? true : false}
				/>
				{isUpdate ? (
					<Form.Item label="Name" required={true}>
						{getFieldDecorator('nameGiven', {
							initialValue: nullSafeGet('supplierContact.nameGiven', formData),
							rules: [{ required: true, message: 'This field is required.' }],
						})(<Input placeholder="First name" style={{ maxWidth: 316 }} />)}
						{getFieldDecorator('nameFamily', {
							initialValue: nullSafeGet('supplierContact.nameFamily', formData),
						})(<Input placeholder="Last name" style={{ maxWidth: 316, marginLeft: 8 }} />)}
					</Form.Item>
				) : (
					<Form.Item label="Name">
						{getFieldDecorator('name', {
							initialValue: formData.name,
							rules: [{ required: true, message: 'This field is required.' }],
						})(<Input style={{ maxWidth: 640 }} />)}
					</Form.Item>
				)}
				<Form.Item label="Role">
					{getFieldDecorator('role', {
						initialValue: formData.role,
					})(<Input style={{ maxWidth: 640 }} />)}
				</Form.Item>
				{/*
					EPA 608 technician certification, on the UPDATE form only.

					It lives on the CONTACT rather than on the field-tech row because
					`RefrigerantEventServiceImpl` resolves a refrigerant event's `technicianEmail` to a
					contact and reads the credential there — that is rule (a) of the 608 write rule, and a
					certification stored anywhere else cannot satisfy it. Recording it here is what lets the
					log-event drawer fill the certification automatically instead of making the tech retype
					their class and number on every event they log.

					Update-only for the same reason the name fields above are: the create path posts a
					field tech before the contact has a user account, and the contact-details PATCH requires
					one. Rendering the fields on create would collect a credential this form could not save.

					Neither half is required — a tech who never touches refrigerant must still be saveable,
					and enforcement belongs at the event write, where the server can tell whether
					refrigerant is actually involved. `allowClear` on the type is intentional: a
					certification can lapse, and there has to be a way to say so.
				*/}
				{isUpdate ? (
					<>
						<Form.Item label="EPA Certification Type">
							{getFieldDecorator('epaCertificationType', {
								initialValue: nullSafeGet('supplierContact.epaCertificationType', formData),
							})(
								<Select
									placeholder="Select certification type"
									allowClear
									style={{ maxWidth: 640 }}
								>
									{EPA_CERTIFICATION_TYPE_OPTIONS.map((option) => (
										<Select.Option value={option.value} key={option.value}>
											{option.label}
										</Select.Option>
									))}
								</Select>
							)}
						</Form.Item>
						<Form.Item label="EPA Certification Number">
							{getFieldDecorator('epaCertificationNumber', {
								initialValue: nullSafeGet('supplierContact.epaCertificationNumber', formData),
							})(
								/* 64 is `epa_certification_number`'s column width; the server rejects longer. */
								<Input
									placeholder="Certification number"
									maxLength={64}
									style={{ maxWidth: 640 }}
								/>
							)}
						</Form.Item>
					</>
				) : null}
				<Form.Item label="Notes">
					{getFieldDecorator('notesOnTech', {
						initialValue: formData.notesOnTech,
					})(<Input.TextArea style={{ maxWidth: 640 }} />)}
				</Form.Item>
				<Form.Item label="Hourly Rate">
					{getFieldDecorator('hourlyRate', {
						initialValue: formData.hourlyRate,
					})(<Input style={{ maxWidth: 640 }} />)}
				</Form.Item>
				<Form.Item label="Available for work?">
					{getFieldDecorator('isAvailable', {
						initialValue: formData.isAvailable === undefined ? false : formData.isAvailable,
						valuePropName: 'checked',
					})(<Switch />)}
				</Form.Item>
				<Form.Item>
					<OWButton
						type="primary"
						htmlType="submit"
						loading={isLoading}
						className="contactForm__button"
					>
						{submitText}
					</OWButton>
					<span style={{ marginLeft: '16px' }}>
						<BackButton buttonText="Cancel" />
					</span>
				</Form.Item>
			</Form>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	formData: ownProps.formData,
	redirectForwardUrl: ownProps.redirectForwardUrl,
	onSuccess: ownProps.onSuccess,

	createErrors: state.field_techs.createErrors,
	updateErrors: state.field_techs.updateErrors,
	creating: state.field_techs.creating,
	updating: state.field_techs.updating,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch) => ({
	update: (entity) => dispatch(fieldTechsRestCrudThunksForSupplier.update(entity)),
	create: (entity) => dispatch(fieldTechsRestCrudThunksForSupplier.create(entity)),
	updateDetails: (payload) => dispatch(updateFieldTechDetailsForSupplier(payload)),
});

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(Form.create<FieldTechFormProps>()(FieldTechForm))
);
